import {
  MultipleChoiceQuestionFormItemResponses,
  UserDisplay,
} from "@coaching-culture/types";
import {
  Box,
  Flex,
  PanelHeader,
  PanelInset,
  StatTile,
  Text,
  typeSize,
} from "@coaching-culture/ui";
import { useUser } from "auth";
import React from "react";
import { FaCheckCircle, FaQuestionCircle } from "react-icons/fa";
import styled from "styled-components";
import { FreqChart } from "./FreqChart";

const Container = styled.div<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 6px 12px;
  margin-bottom: 6px;
  font-size: ${typeSize("section")};
  border-radius: 8px;
  border: 1px solid
    ${(props) =>
      props.active ? props.theme.colors.primary : props.theme.colors.border};
  background-color: ${(props) =>
    props.active ? props.theme.colors.primary : props.theme.colors.surface};
  color: ${(props) =>
    props.active ? props.theme.colors.surface : props.theme.colors.textMuted};
  width: 100%;
`;

export type MultipleChoiceInsightsProps = {
  item: MultipleChoiceQuestionFormItemResponses;
  user: UserDisplay;
};

export function MultipleChoiceInsights({
  item,
  user,
}: MultipleChoiceInsightsProps) {
  const [currentUser] = useUser();

  const userText = currentUser?.id === user?.id ? "Your" : "User's";
  const getAnswer = (id: string | null) =>
    item.answers.find((x) => x.id === id)?.content ?? "None";

  const responses = item.responses;

  const yourAnswer = item.responses.find((x) => x.email === user?.email);

  const withoutMe = responses.filter((x) => x.email !== user?.email);

  const responseCount = withoutMe.length;

  const data = item.answers.map((x) => ({
    y: x.content,
    x: withoutMe.filter((w) => w.answer === x.id).length,
  }));

  return (
    <PanelInset mb={3}>
      <PanelHeader>
        <FaQuestionCircle aria-hidden />
        <Text fontWeight={600} ml={2} color="black">
          {item.content}
        </Text>
      </PanelHeader>
      <Box p={3} pb={0} maxWidth="272px">
        <StatTile label="Responses" value={responseCount} />
      </Box>
      <Flex flexDirection="column" p={3} justifyContent="stretch">
        {user != null && (
          <Box mb={2}>
            <Text fontWeight={500} fontSize="small" color="textMuted" mb={1}>
              {userText} answer
            </Text>
            <Container active={yourAnswer != null}>
              {yourAnswer != null ? <FaCheckCircle /> : <FaQuestionCircle />}
              <Text ml={2} fontSize="title" color="inherit">
                {getAnswer(yourAnswer?.answer)}
              </Text>
            </Container>
          </Box>
        )}
        <Flex width="100%" flexDirection="column">
          <Text fontWeight={500} fontSize="small" color="textMuted" mb={1}>
            Answers
          </Text>
          <FreqChart data={data} />
        </Flex>
      </Flex>
    </PanelInset>
  );
}
